<script lang="ts" setup>
import { Card } from '@vue-interface/card';
import { InputField } from '@vue-interface/input-field';
import { LightSwitchField } from '@vue-interface/light-switch-field';
import { useForm } from 'laravel-precognition-vue-inertia';
import type { Agency } from 'types';
import { ref } from 'vue';
import FormField from './FormField.vue';
import FormLayout from './FormLayout.vue';

export type PbtGroupSettings = {
    name: string;
    description: string | null;
    info: boolean;
    chatEnabled: boolean;
    optinMessage: string | null;
    stopMessage: string | null;
    helpMessage: string | null;
    notificationEmails: string[];
};

const props = withDefaults(defineProps<{
    url: string;
    agency: Agency;
    group?: PbtGroupSettings;
    method?: 'post' | 'put';
}>(), {
    group: undefined,
    method: 'post',
});

const isEditing = props.group !== undefined;

const notificationEmailsRaw = ref<string>(props.group?.notificationEmails.join(', ') ?? '');

const form = useForm(props.method, props.url, {
    name: props.group?.name ?? '',
    description: props.group?.description ?? '',
    info: props.group?.info ?? false,
    chatEnabled: props.group?.chatEnabled ?? false,
    optinMessage: props.group?.optinMessage ?? '',
    stopMessage: props.group?.stopMessage ?? '',
    helpMessage: props.group?.helpMessage ?? '',
    notificationEmails: [] as string[],
    configureWebhooks: false,
});

// The group type can't change after creation, so an edit doesn't send it.
// Webhooks are always registered on create, so only an edit sends the choice.
// @ts-ignore
form.transform(({ info, configureWebhooks, ...data }) => ({
    ...data,
    ...(isEditing ? { configureWebhooks } : { info }),
    notificationEmails: notificationEmailsRaw.value
        .split(/[\n,]+/)
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0),
}));

function onSubmit() {
    form.submit();
}
</script>

<template>
    <FormLayout
        :submit="onSubmit"
        :button-label="isEditing ? 'Save' : 'Submit'"
        :disabled="form.processing"
        :activity="form.processing">
        <Card>
            <FormField
                label="Name"
                description="The list name. Also used as the group name on Powered By Text."
                required>
                <InputField
                    id="name"
                    v-model="form.name"
                    name="name"
                    :error="form.errors.name"
                    @change="form.validate('name')" />
            </FormField>
            <FormField
                label="Description"
                description="Optional description for this list.">
                <InputField
                    id="description"
                    v-model="form.description"
                    name="description"
                    :error="form.errors.description"
                    @change="form.validate('description')" />
            </FormField>
            <FormField
                v-if="!isEditing"
                label="Info Group"
                description="Info groups are one-way (broadcast only). Activity groups support replies.">
                <LightSwitchField
                    id="info"
                    v-model="form.info"
                    name="info"
                    size="form-switch-xs"
                    :off-value="false"
                    :on-value="true"
                    :error="form.errors.info"
                    @update:model-value="form.validate('info')" />
            </FormField>
            <FormField
                label="Chat Enabled"
                description="Allow two-way conversations with subscribers.">
                <LightSwitchField
                    id="chatEnabled"
                    v-model="form.chatEnabled"
                    name="chatEnabled"
                    size="form-switch-xs"
                    :off-value="false"
                    :on-value="true"
                    :error="(form.errors as Record<string,string>).chatEnabled"
                    @update:model-value="form.validate('chatEnabled' as never)" />
            </FormField>
            <FormField
                label="Opt-In Message"
                description="Message sent when a subscriber joins. PBT uses a default if left blank.">
                <InputField
                    id="optinMessage"
                    v-model="form.optinMessage"
                    name="optinMessage"
                    :error="(form.errors as Record<string,string>).optinMessage"
                    @change="form.validate('optinMessage' as never)" />
            </FormField>
            <FormField
                label="STOP Message"
                description="Message sent when a subscriber texts STOP. PBT uses a default if left blank.">
                <InputField
                    id="stopMessage"
                    v-model="form.stopMessage"
                    name="stopMessage"
                    :error="(form.errors as Record<string,string>).stopMessage"
                    @change="form.validate('stopMessage' as never)" />
            </FormField>
            <FormField
                label="HELP Message"
                description="Message sent when a subscriber texts HELP. PBT uses a default if left blank.">
                <InputField
                    id="helpMessage"
                    v-model="form.helpMessage"
                    name="helpMessage"
                    :error="(form.errors as Record<string,string>).helpMessage"
                    @change="form.validate('helpMessage' as never)" />
            </FormField>
            <FormField
                label="Notification Emails"
                description="Comma- or newline-separated. Receive notifications when subscribers reply.">
                <InputField
                    id="notificationEmailsRaw"
                    v-model="notificationEmailsRaw"
                    name="notificationEmailsRaw"
                    :error="(form.errors as Record<string,string>)['notificationEmails.0']" />
            </FormField>
            <FormField
                v-if="isEditing"
                label="Reconfigure Webhooks"
                description="Register this list's webhooks again when you save. Use this if replies or delivery statuses stop arriving.">
                <LightSwitchField
                    id="configureWebhooks"
                    v-model="form.configureWebhooks"
                    name="configureWebhooks"
                    size="form-switch-xs"
                    :off-value="false"
                    :on-value="true"
                    :error="(form.errors as Record<string,string>).configureWebhooks" />
            </FormField>
        </Card>
    </FormLayout>
</template>
